export interface IMonthYear {
    year: number;
    month: number;
}

export function parseMonthYear(raw: any): IMonthYear | null {
    if (typeof raw !== "string") {
        return null;
    }
    const value = raw.trim();
    let match = value.match(/^(\d{1,2})\s*[\/.\-]\s*(\d{4})$/);
    if (match) {
        return buildMonthYear(parseInt(match[2], 10), parseInt(match[1], 10));
    }
    match = value.match(/^(\d{4})-(\d{1,2})(?:-\d{1,2})?(?:[T ].*)?$/);
    if (match) {
        return buildMonthYear(parseInt(match[1], 10), parseInt(match[2], 10));
    }
    match = value.match(/^\d{1,2}\.(\d{1,2})\.(\d{4})$/);
    if (match) {
        return buildMonthYear(parseInt(match[2], 10), parseInt(match[1], 10));
    }
    return null;
}

function buildMonthYear(year: number, month: number): IMonthYear | null {
    if (!Number.isFinite(year) || !Number.isFinite(month) || month < 1 || month > 12) {
        return null;
    }
    return { year, month };
}

function pad(month: number): string {
    return month < 10 ? `0${month}` : `${month}`;
}

export function formatMonthYear(raw: any): string {
    const parsed = parseMonthYear(raw);
    return parsed ? `${pad(parsed.month)}/${parsed.year}` : "";
}

export function toStoredMonthYear(raw: any): string {
    const parsed = parseMonthYear(raw);
    return parsed ? `${parsed.year}-${pad(parsed.month)}-01` : "";
}

export function maskMonthYearInput(raw: string): string {
    const digits = (raw || "").replace(/\D/g, "").slice(0, 6);
    if (digits.length <= 2) {
        return digits;
    }
    return `${digits.slice(0, 2)}/${digits.slice(2)}`;
}

export function addYears(value: IMonthYear, years: number): IMonthYear {
    return { year: value.year + years, month: value.month };
}

export function isMonthYearDueBy(
    value: IMonthYear,
    referenceDate: Date,
    leadMonths: number
): boolean {
    const due = value.year * 12 + (value.month - 1);
    const limit = referenceDate.getFullYear() * 12 + referenceDate.getMonth() + leadMonths;
    return due <= limit;
}

export const DUE_LEAD_MONTHS = 12;

export interface IDueStatus {
    manufactured: string;
    expires: string;
    overdue: boolean;
    offered: boolean;
}

export function dueStatus(raw: any, lifetimeYears: number, referenceDate: Date): IDueStatus | null {
    const parsed = parseMonthYear(raw);
    if (!parsed) {
        return null;
    }
    const expires = addYears(parsed, lifetimeYears);
    const now = referenceDate.getFullYear() * 12 + referenceDate.getMonth();
    const expiresIndex = expires.year * 12 + (expires.month - 1);
    return {
        manufactured: `${pad(parsed.month)}/${parsed.year}`,
        expires: `${pad(expires.month)}/${expires.year}`,
        overdue: expiresIndex <= now,
        offered: expiresIndex <= now + DUE_LEAD_MONTHS,
    };
}
